/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useState } from 'react';
import { useDeviceInfo } from '@airasia-rn/ui';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';

type DesktopWebHoverProps = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

// Touch screens fire emulated mouse events on tap, so width alone would leave a sticky hover on
// tablets / landscape phones. Only a real mouse or trackpad matches this query.
const hasFinePointerHover = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Hover state for desktop web only. On native and mobile web it is always off and `hoverProps` is
 * empty, so the component renders exactly as it would without hover.
 *
 * Spread `hoverProps` onto a View / Touchable* (react-native-web forwards web mouse props).
 *
 * @param enabled - caller's own condition, e.g. `!disabled` or `!selected`
 */
export const useDesktopWebHover = (enabled = true) => {
  const { isWeb, isMobileBrowser } = useDeviceInfo();
  const isHoverEnabled =
    enabled &&
    isWeb &&
    !isMobileBrowser &&
    getCurrentBreakpoint() > 1 &&
    hasFinePointerHover();

  const [isHovered, setIsHovered] = useState(false);

  const hoverProps: DesktopWebHoverProps = isHoverEnabled
    ? {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
      }
    : {};

  return {
    isHoverEnabled,
    isHovered: isHoverEnabled && isHovered,
    hoverProps,
  };
};
